import { AnalyticEvent, AnalyticPayloads, eventsMetadata } from './registry';
import { AnalyticsType } from '@/utils/analytics';
import { BasePayload } from './types';
import { captureException } from '@/utils/sentry';

/**
 * Fennel tracker is a singleton class that tracks user events and sends them to the analytics service.
 */
export class Fennel {
  private static instance: Fennel;
  private state: BasePayload | null = null;
  private engine: AnalyticsType | null = null;

  private constructor() {
    // Private constructor to prevent instantiation
  }

  public static getInstance(): Fennel {
    if (!Fennel.instance) {
      Fennel.instance = new Fennel();
    }
    return Fennel.instance;
  }

  /**
   * Initializes the base properties of the Fennel tracker & event logging engine.
   */
  public initialize(initialData: BasePayload, engine: AnalyticsType): void {
    this.state = this.state ? { ...this.state, ...initialData } : initialData;
    this.engine = engine;
  }

  /**
   * Tracks a user event and sends it to the analytics service.
   *
   * @param event The event to track, based on the registered enum values defined.
   * @param payload The data to send with the event. This data is merged with the base state.
   */
  public async track<E extends AnalyticEvent>(
    event: E,
    payload: AnalyticPayloads[E],
  ) {
    if (!this.state || !this.engine) {
      console.warn('Fennel tracker not initialized. Call initialize() first.');
      captureException(
        new Error('Fennel tracker not initialized. Call initialize() first.'),
      );
      return;
    }

    const meta = eventsMetadata[event];
    this.engine.track(meta.eventName, { ...this.state, ...payload });
  }

  /**
   * Returns the initialized engine and base state, or null if Fennel has not been initialized.
   * Use this to implement experience-specific tracking wrappers that need access to the shared state.
   */
  public getInitializationState(): {
    engine: AnalyticsType;
    state: BasePayload;
  } | null {
    if (!this.state || !this.engine) {
      return null;
    }
    return { engine: this.engine, state: this.state };
  }
}
